import { Controller } from "@hotwired/stimulus"

// Saga's side of the meeting arrives a word at a time, the way her replies
// do. Only the lines marked as targets animate — the open turn, never the
// ones already answered — and the merchant's way of answering stays out of
// sight until she has finished asking. Reduced motion shows everything at
// once.
//
// The line being written, and then the way to answer it, are kept inside
// the viewport as they grow, the way a chat keeps its newest message in
// view — the merchant never scrolls to find out what Saga just asked.
export default class extends Controller {
  static targets = ["line", "reply"]
  static values = {
    wordDelay: { type: Number, default: 55 },
    lineDelay: { type: Number, default: 450 },
    pauseBefore: { type: Number, default: 500 },
    margin: { type: Number, default: 96 }
  }

  connect() {
    this.lines = this.lineTargets.map(el => ({ el, words: el.textContent.trim().split(/\s+/) }))

    if (this.lines.length === 0 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.finish()
      return
    }

    this.replyTargets.forEach(el => el.classList.add("invisible"))
    this.lines.forEach(({ el }) => { el.textContent = ""; el.classList.add("hidden") })
    this.timer = setTimeout(() => this.typeLine(0), this.pauseBeforeValue)
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  typeLine(index) {
    if (index >= this.lines.length) {
      this.finish()
      return
    }

    const { el, words } = this.lines[index]
    el.classList.remove("hidden")
    el.classList.add("typewriter-cursor")
    let shown = 0
    const tick = () => {
      shown += 1
      el.textContent = words.slice(0, shown).join(" ")
      this.keepInView(el)
      if (shown < words.length) {
        this.timer = setTimeout(tick, this.wordDelayValue)
      } else {
        el.classList.remove("typewriter-cursor")
        this.timer = setTimeout(() => this.typeLine(index + 1), this.lineDelayValue)
      }
    }
    tick()
  }

  finish() {
    this.lines.forEach(({ el, words }) => {
      el.textContent = words.join(" ")
      el.classList.remove("typewriter-cursor", "hidden")
    })
    this.replyTargets.forEach(el => el.classList.remove("invisible"))
    const reply = this.replyTargets[0]
    if (!reply) return
    this.keepInView(reply)
    reply.querySelector("input:not([type=hidden]), textarea")?.focus({ preventScroll: true })
  }

  // Scroll only as far as needed for the element's bottom edge to sit above
  // the viewport's, with room to spare — never up, never past it.
  keepInView(el) {
    const overflow = el.getBoundingClientRect().bottom - (window.innerHeight - this.marginValue)
    if (overflow > 0) window.scrollBy(0, overflow)
  }
}
